import { ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Card, Modal, Skeleton, Space, Switch, Tabs, Tag, Typography, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { errorMessage } from '../api/client';
import { useDealer, useDeleteDealer, useUpdateDealerStatus } from '../api/hooks';
import DealerInvestmentsTab from '../components/dealer/DealerInvestmentsTab';
import DealerLedgerTab from '../components/dealer/DealerLedgerTab';
import DealerProfileTab from '../components/dealer/DealerProfileTab';
import DealerProfitTab from '../components/dealer/DealerProfitTab';
import DealerReceiptsTab from '../components/dealer/DealerReceiptsTab';
import DealerWithdrawalsTab from '../components/dealer/DealerWithdrawalsTab';

/**
 * A single dealer's page — deliberately built exactly like InvestorDetailPage: same header layout,
 * same tab order (Profile, Investments, Profit, Withdrawals, Ledger, Receipts), same Active switch.
 * The two sides of the money should read the same way, not need two different mental models.
 */
export default function DealerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const dealerId = Number(id);
  const navigate = useNavigate();
  const { data } = useDealer(dealerId);
  const updateStatus = useUpdateDealerStatus(dealerId);
  const deleteDealer = useDeleteDealer();

  const toggleStatus = async (active: boolean) => {
    try {
      await updateStatus.mutateAsync(active ? 'Active' : 'Inactive');
      message.success(`Dealer ${active ? 'activated' : 'deactivated'}.`);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  const onDelete = () => {
    if (!data) return;
    Modal.confirm({
      title: `Delete ${data.dealerCode} — ${data.fullName}?`,
      content:
        'This permanently removes the dealer along with every investment, withdrawal and profit payment on record. This cannot be undone.',
      okText: 'Delete permanently',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteDealer.mutateAsync(dealerId);
          message.success('Dealer deleted.');
          navigate('/my-investments');
        } catch (err) {
          Modal.error({ title: 'Could not delete this dealer', content: errorMessage(err) });
        }
      },
    });
  };

  if (!data) return <Skeleton active avatar paragraph={{ rows: 8 }} />;

  return (
    <>
      <Space style={{ marginBottom: 16, flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/my-investments')}>
            Back
          </Button>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {data.dealerCode} — {data.fullName}
          </Typography.Title>
          <Tag color={data.status === 'Active' ? 'green' : 'default'}>{data.status}</Tag>
        </Space>

        <Space>
          <span>Active</span>
          <Switch checked={data.status === 'Active'} loading={updateStatus.isPending} onChange={toggleStatus} />
          <Button danger icon={<DeleteOutlined />} loading={deleteDealer.isPending} onClick={onDelete}>
            Delete
          </Button>
        </Space>
      </Space>

      <Card>
        <Tabs
          defaultActiveKey="profile"
          items={[
            { key: 'profile', label: 'Profile', children: <DealerProfileTab dealerId={dealerId} /> },
            { key: 'investments', label: 'Investments', children: <DealerInvestmentsTab dealerId={dealerId} /> },
            { key: 'profit', label: 'Profit', children: <DealerProfitTab dealerId={dealerId} /> },
            { key: 'withdrawals', label: 'Withdrawals', children: <DealerWithdrawalsTab dealerId={dealerId} /> },
            { key: 'ledger', label: 'Ledger', children: <DealerLedgerTab dealerId={dealerId} /> },
            { key: 'receipts', label: 'Receipts', children: <DealerReceiptsTab dealerId={dealerId} /> },
          ]}
        />
      </Card>
    </>
  );
}
